<script setup lang="ts">
const { t, tm, rt, locale } = useI18n()

type Item = { name: string; from: string; old: string; new: string; save: string; icon: string }
const items = computed(() =>
  (tm('products.items') as any[]).map((it) => ({
    name: rt(it.name),
    from: rt(it.from),
    old:  rt(it.old),
    new:  rt(it.new),
    save: rt(it.save),
    icon: rt(it.icon),
  })) as Item[],
)

// Compute the absolute savings from the strikethrough price minus the new
// price. The i18n only carries the percentage, but customers care more about
// the euro figure ("you save €310" reads stronger than "−22%"). Strip every
// non-digit before parsing so the same routine handles "€1,400", "€1 400"
// (RU), "1.400 €" (DE) etc.
function savedAmount(item: Item): string {
  const oldN = parseInt(item.old.replace(/[^\d]/g, ''), 10)
  const newN = parseInt(item.new.replace(/[^\d]/g, ''), 10)
  if (!Number.isFinite(oldN) || !Number.isFinite(newN) || oldN <= newN) return ''
  const diff = oldN - newN
  return `€${diff.toLocaleString(locale.value)}`
}
</script>

<template>
  <section id="products" class="rod-section relative">
    <div class="rod-container">
      <div class="text-center max-w-3xl mx-auto mb-14" data-reveal>
        <div class="rod-eyebrow mb-5">🛍️ {{ t('products.eyebrow') }}</div>
        <h2 class="rod-h2 mb-5">
          <span v-html="t('products.title')" />
        </h2>
        <p class="text-lg text-[var(--color-text-muted)]">{{ t('products.lead') }}</p>
      </div>

      <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div v-for="(item, i) in items" :key="i" class="rod-card group relative overflow-hidden" data-reveal>
          <div class="absolute top-4 right-4 rod-save-badge">{{ item.save }}</div>

          <div class="text-5xl mb-5">{{ item.icon }}</div>

          <div class="text-xs text-[var(--color-text-dim)] mb-1 uppercase tracking-wider">{{ item.from }}</div>
          <div class="font-display font-semibold text-lg mb-4 leading-tight">{{ item.name }}</div>

          <div class="flex items-baseline gap-3">
            <span class="text-2xl font-display font-bold rod-gradient-text">{{ item.new }}</span>
            <span class="text-sm text-[var(--color-text-dim)] line-through">{{ item.old }}</span>
          </div>

          <div class="mt-3 text-xs text-[var(--color-text-muted)]">
            {{ t('products.saveLabel') }}
            <span class="font-semibold rod-gradient-text">{{ savedAmount(item) }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
